/**
 * Verification Log verb builders (13-11, 13-11.1): verify / reject an agent-write evidence row,
 * and correct a mistaken verdict the same day. Pure.
 *
 *   - `vlVerify(row, actor, now, verdict = 'verified')` and `vlReject(row, actor, now, reason)`
 *     return `{ patch, audit[] }`: the If-Match PATCH and one `StateChange` audit draft per changed
 *     field (`ParentRef = VER-<id>`).
 *   - **The append-only rule (09.1 §4, ISS-116), asserted:** the patch keys are a subset of
 *     `VL_MUTABLE_FIELDS` (`VerifiedBy` as its Graph name `VerifiedByLookupId`). Anything else
 *     throws before a request could be built.
 *   - Only `Verdict = pending` rows change (`already-verified` otherwise).
 *   - **Agent rows never self-verify (E4):** an actor whose UPN is the row's `AgentIdentity` is
 *     refused (`self-verification`). UPNs compare ignoring case and surrounding space (D-13-05-5).
 *   - **Who verifies (D-13-R-6, superseding D-13-08-2 / D-13-11-1's Oversight-only rule for VL
 *     rows):** Oversight, or a holder of the row's `VerifierRole` (`mayVerifyEvidence`). A blank
 *     or no-App-Role `VerifierRole` is Oversight only (`oversight-only`); another role's row is
 *     `not-verifier-role`. `VerifierRole` is stamped only when it is blank, and then as
 *     `Oversight`, the only role that could act on it (D-13-11-2 as amended).
 *   - A rejection needs its own reason, at least `MIN_REASON_CHARS` characters, written to
 *     `ExceptionReason` (and the audit Note).
 *   - **`vlCorrect` (D-13-R-5, ISS-193):** the person who gave the verdict, or Oversight, turns
 *     `verified` / `sampled-ok` into `rejected` or back, with a required reason, only on the same
 *     `Australia/Melbourne` calendar day as `VerifiedAt` (mirrors D-13-04-4). After that,
 *     "Corrections after the day need Oversight in the register".
 *
 * Also here, because the Verify page needs them and they must be testable without React:
 * `parseRecordRef`, `workingDaysSince` (E4's 5-working-day window) and `linkedActionGate`, the
 * ISS-188 caller limb for rows whose `RecordRef` is an Action Register row.
 *
 * `actionService.buildVlVerify` delegates here, so the live writer and the practice-mode writer
 * build exactly these patches.
 */

import { OWNER_ROLE_APP_ROLE, completerGate, mayVerifyEvidence } from './actionAuthority';
import { VL_FIELDS, VL_LOOKUP_FIELDS, VL_MUTABLE_FIELDS, verificationRef } from '../config/actionRegister';
import type { AuthorityReason } from './actionAuthority';
import { VerbError, melbourneDay } from './actionVerbs';
import type { ActedAt } from './actionVerbs';
import type {
  ActionRow,
  ActionStatus,
  Actor,
  AuditRowDraft,
  OwnerRole,
  VerbResult,
  VerificationRow,
  VerificationVerdict,
} from '../types/actionRegister';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * The role stamped on a row whose `VerifierRole` was blank (D-13-11-2 as amended by D-13-R-6). A
 * blank row can only be verified by Oversight, so that is the role the verdict was given in. A set
 * `VerifierRole` is never overwritten.
 */
export const VL_VERIFIER_ROLE: OwnerRole = 'Oversight';

/** The verdicts `vlCorrect` sets. `sampled-ok` counts as `verified` for "is this a change?". */
export const VL_CORRECT_VERDICTS = ['verified', 'rejected'] as const;
export type VlCorrectVerdict = (typeof VL_CORRECT_VERDICTS)[number];

/** Verdicts a person gave, and so can correct (`packet-delivered` is a card's, `pending` none). */
const CORRECTABLE: readonly VerificationVerdict[] = ['verified', 'sampled-ok', 'rejected'];

/** A rejection's reason must be at least this long (trimmed). */
export const MIN_REASON_CHARS = 10;

/** The verdicts `vlVerify` writes. A rejection is `vlReject`, with its reason. */
export const VL_POSITIVE_VERDICTS = ['verified', 'sampled-ok'] as const;
export type VlPositiveVerdict = (typeof VL_POSITIVE_VERDICTS)[number];

/** E4's window: pending evidence older than this many working days is overdue. */
export const E4_WINDOW_WORKING_DAYS = 5;

/** The keys a VL patch may carry: `VL_MUTABLE_FIELDS`, with the Person column as its LookupId. */
export const VL_PATCH_KEYS: ReadonlySet<string> = new Set<string>(
  VL_MUTABLE_FIELDS.map((f) => (f === VL_FIELDS.verifiedBy ? VL_LOOKUP_FIELDS.verifiedBy : f)),
);

// ---------------------------------------------------------------------------
// The append-only assertion
// ---------------------------------------------------------------------------

/** Throws unless every key of `patch` is one of the five permitted VL fields (ISS-116). */
export function assertVlPatchKeys(patch: Record<string, unknown>): void {
  const bad = Object.keys(patch).filter((k) => !VL_PATCH_KEYS.has(k));
  if (bad.length > 0) {
    throw new Error(`Verification Log is append-only: refusing to change ${bad.join(', ')}`);
  }
}

// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------

function sameUpn(a: string | null | undefined, b: string | null | undefined): boolean {
  const x = (a ?? '').trim().toLowerCase();
  const y = (b ?? '').trim().toLowerCase();
  return x !== '' && x === y;
}

/** Is the row for Oversight alone (a blank `VerifierRole`, or one with no App Role)? */
function oversightOnlyRow(row: VerificationRow): boolean {
  const role = row.verifierRole;
  return role == null || String(role).trim() === '' || OWNER_ROLE_APP_ROLE[role] == null;
}

/** D-13-R-6: Oversight, or a holder of the row's VerifierRole. Throws the precise refusal. */
function requireVerifier(row: VerificationRow, actor: Actor, verb: string): void {
  if (mayVerifyEvidence(row.verifierRole, actor)) return;
  if (oversightOnlyRow(row)) {
    throw new VerbError('oversight-only', `${verb}: VER-${row.id} is verified by Oversight`);
  }
  throw new VerbError('not-verifier-role', `${verb}: VER-${row.id} is verified by ${row.verifierRole} or Oversight`);
}

/** E4: never the row's own AgentIdentity. */
function requireNotAgent(row: VerificationRow, actor: Actor, verb: string): void {
  if (sameUpn(actor.upn, row.agentIdentity)) {
    throw new VerbError('self-verification', `${verb}: VER-${row.id} was written by ${actor.upn}`);
  }
}

function guard(row: VerificationRow, actor: Actor, verb: string): void {
  requireVerifier(row, actor, verb);
  if (row.verdict !== 'pending') {
    throw new VerbError('already-verified', `${verb}: VER-${row.id} is already ${row.verdict}`);
  }
  requireNotAgent(row, actor, verb);
}

// ---------------------------------------------------------------------------
// The builder
// ---------------------------------------------------------------------------

const FIELD_READ: Record<string, (r: VerificationRow) => unknown> = {
  [VL_FIELDS.verdict]: (r) => r.verdict,
  [VL_FIELDS.verifiedAt]: (r) => r.verifiedAt,
  [VL_FIELDS.verifierRole]: (r) => r.verifierRole,
  [VL_LOOKUP_FIELDS.verifiedBy]: (r) => r.verifiedByLookupId,
  [VL_FIELDS.exceptionReason]: (r) => r.exceptionReason,
};

function isEmpty(v: unknown): boolean {
  return v === null || v === undefined || v === '';
}

function sameValue(prev: unknown, next: unknown): boolean {
  if (isEmpty(prev) || isEmpty(next)) return isEmpty(prev) && isEmpty(next);
  return String(prev) === String(next);
}

function build(
  row: VerificationRow,
  actor: Actor,
  now: ActedAt,
  verdict: VerificationVerdict,
  verb: 'verify' | 'reject' | 'correct',
  reason: string | null,
): VerbResult {
  const at = new Date(now).toISOString();
  const proposed: Record<string, unknown> = {
    [VL_FIELDS.verdict]: verdict,
    [VL_FIELDS.verifiedAt]: at,
  };
  // D-13-11-2 as amended by D-13-R-6: stamp VerifierRole only when it is blank.
  if (isEmpty(row.verifierRole)) proposed[VL_FIELDS.verifierRole] = VL_VERIFIER_ROLE;
  if (actor.lookupId) proposed[VL_LOOKUP_FIELDS.verifiedBy] = actor.lookupId;
  // A correction by someone whose site id did not resolve must not leave the earlier verifier
  // named as VerifiedBy; the UPN in every audit Note says who it was (D-13-00-4).
  else if (verb === 'correct') proposed[VL_LOOKUP_FIELDS.verifiedBy] = null;
  if (verdict === 'rejected') proposed[VL_FIELDS.exceptionReason] = reason;
  // A correction back to verified clears the rejection's reason; it survives as the audit OldValue.
  else if (verb === 'correct') proposed[VL_FIELDS.exceptionReason] = null;

  const parentRef = verificationRef(row.id);
  const label = verb === 'verify' && verdict !== 'verified' ? `verify (${verdict})` : verb;
  const note = reason ? `${label} by ${actor.upn}: ${reason}` : `${label} by ${actor.upn}`;

  const patch: Record<string, unknown> = {};
  const audit: AuditRowDraft[] = [];
  for (const [key, next] of Object.entries(proposed)) {
    const prev = FIELD_READ[key](row);
    if (sameValue(prev, next)) continue;
    patch[key] = next;
    const column = key === VL_LOOKUP_FIELDS.verifiedBy ? VL_FIELDS.verifiedBy : key;
    const draft: AuditRowDraft = {
      title: `${verb} ${parentRef}: ${column}`,
      parentRef,
      entryTimestamp: at,
      entryType: 'StateChange',
      fieldChanged: column,
      oldValue: isEmpty(prev) ? null : String(prev),
      newValue: isEmpty(next) ? null : String(next),
      note,
    };
    if (actor.lookupId) draft.actorLookupId = actor.lookupId;
    audit.push(draft);
  }
  assertVlPatchKeys(patch);
  return { patch, audit };
}

/** Verify a pending Verification Log row (`verified`, or `sampled-ok` for a sampled check). */
export function vlVerify(
  row: VerificationRow,
  actor: Actor,
  now: ActedAt,
  verdict: VlPositiveVerdict = 'verified',
): VerbResult {
  if (!(VL_POSITIVE_VERDICTS as readonly string[]).includes(verdict)) {
    throw new VerbError('invalid-verdict', `verify: ${String(verdict)} is not a verify verdict`);
  }
  guard(row, actor, 'verify');
  return build(row, actor, now, verdict, 'verify', null);
}

/** Reject a pending Verification Log row. The reason is required (≥ `MIN_REASON_CHARS`). */
export function vlReject(row: VerificationRow, actor: Actor, now: ActedAt, reason: string): VerbResult {
  const why = (reason ?? '').trim();
  if (why.length < MIN_REASON_CHARS) {
    throw new VerbError('reason-required', `reject: VER-${row.id} needs a reason of ${MIN_REASON_CHARS}+ characters`);
  }
  guard(row, actor, 'reject');
  return build(row, actor, now, 'rejected', 'reject', why);
}

// ---------------------------------------------------------------------------
// vlCorrect (ISS-193 / D-13-R-5)
// ---------------------------------------------------------------------------

export interface VlCorrectContext {
  /**
   * The UPN that gave the current verdict, when the caller knows it without the row (this session
   * gave it, as D-13-09-2 does for reopen). Otherwise "who gave it" is the row's `VerifiedBy`
   * lookup id, compared with the actor's.
   */
  verdictByUpn?: string;
}

/** Why a verdict can't be corrected by this actor now (the reason and the new verdict aside). */
export type CorrectionRefusal = 'no-verdict' | 'not-verifier' | 'self-verification' | 'outside-window';

const isPositive = (v: VerificationVerdict) => v === 'verified' || v === 'sampled-ok';

/** Did `actor` give the row's current verdict? Never true on an unproven identity. */
export function gaveVerdict(row: VerificationRow, actor: Actor, ctx: VlCorrectContext = {}): boolean {
  const lookup = (actor.lookupId ?? '').trim();
  if (lookup !== '' && lookup === (row.verifiedByLookupId ?? '').trim()) return true;
  return sameUpn(ctx.verdictByUpn, actor.upn);
}

/** Is `now` on the same `Australia/Melbourne` calendar day as the row's `VerifiedAt`? */
export function inCorrectionWindow(row: Pick<VerificationRow, 'verifiedAt'>, now: ActedAt): boolean {
  if (!row.verifiedAt || Number.isNaN(Date.parse(row.verifiedAt))) return false;
  return melbourneDay(row.verifiedAt) === melbourneDay(now);
}

/**
 * May `actor` correct this row's verdict right now? Everything `vlCorrect` checks except the reason
 * and the new verdict, so the page can offer "Correct this" (or say why not) before one is typed.
 */
export function correctionRefusal(
  row: VerificationRow,
  actor: Actor,
  now: ActedAt,
  ctx: VlCorrectContext = {},
): CorrectionRefusal | null {
  if (!CORRECTABLE.includes(row.verdict)) return 'no-verdict';
  if (!actor.roles.includes('Oversight') && !gaveVerdict(row, actor, ctx)) return 'not-verifier';
  if (sameUpn(actor.upn, row.agentIdentity)) return 'self-verification';
  if (!inCorrectionWindow(row, now)) return 'outside-window';
  return null;
}


/**
 * Correct a mistaken Verification Log verdict (D-13-R-5): `verified` / `sampled-ok` ↔ `rejected`.
 *
 *   - **Who:** the person who gave the verdict (`gaveVerdict`), or Oversight (`not-verifier`).
 *   - **Never the row's own `AgentIdentity`** (E4, `self-verification`).
 *   - **When:** the same Melbourne calendar day as `VerifiedAt` (`outside-window`; DST-safe,
 *     mirrors D-13-04-4). Afterwards it is "Corrections after the day need Oversight in the
 *     register".
 *   - **A reason** of at least `MIN_REASON_CHARS` (`reason-required`); it goes in every audit Note
 *     and, on a rejection, `ExceptionReason`.
 *   - Sets the new `Verdict`, `VerifiedAt = now`, `VerifiedBy` (the actor, or cleared when their
 *     site id is unknown), `ExceptionReason` (the reason on a reject; cleared on a return to
 *     verified), and `VerifierRole` only when blank. Five fields at most (ISS-116), one
 *     `StateChange` draft per changed field, each Note `correct by <upn>: <reason>`.
 *   - `no-change` when the verdict would not change; `no-verdict` on a pending or
 *     packet-delivered row; `invalid-verdict` for anything but `verified` / `rejected`.
 *
 * The linked-action completer gate (ISS-188) is the caller's, as for verify (`linkedActionGate`).
 */
export function vlCorrect(
  row: VerificationRow,
  newVerdict: VlCorrectVerdict,
  reason: string,
  actor: Actor,
  now: ActedAt,
  ctx: VlCorrectContext = {},
): VerbResult {
  if (!(VL_CORRECT_VERDICTS as readonly string[]).includes(newVerdict)) {
    throw new VerbError('invalid-verdict', `correct: ${String(newVerdict)} is not a correction verdict`);
  }
  const refusal = correctionRefusal(row, actor, now, ctx);
  if (refusal) throw new VerbError(refusal, `correct: VER-${row.id} refused (${refusal})`);
  if (isPositive(row.verdict) === isPositive(newVerdict)) {
    throw new VerbError('no-change', `correct: VER-${row.id} is already ${row.verdict}`);
  }
  const why = (reason ?? '').trim();
  if (why.length < MIN_REASON_CHARS) {
    throw new VerbError('reason-required', `correct: VER-${row.id} needs a reason of ${MIN_REASON_CHARS}+ characters`);
  }
  return build(row, actor, now, newVerdict, 'correct', why);
}

// ---------------------------------------------------------------------------
// RecordRef
// ---------------------------------------------------------------------------

export type ParsedRecordRef =
  | { kind: 'action'; id: number; ref: string }
  | { kind: 'incident'; ref: string }
  | { kind: 'other'; ref: string };

/**
 * `ActionRegister/<id>` (or `ActionRegister/ACT-<id>`) → the Action Register row;
 * `Incidents/<id>` (or `…/INC-<id>`, `IncidentRegister/…`) → the incident reference.
 */
export function parseRecordRef(recordRef: string | null | undefined): ParsedRecordRef {
  const ref = (recordRef ?? '').trim();
  const ar = /^ActionRegister\/(?:ACT-)?(\d+)$/i.exec(ref);
  if (ar) return { kind: 'action', id: Number(ar[1]), ref: `ACT-${Number(ar[1])}` };
  const inc = /^(?:Incidents|IncidentRegister)\/(?:INC-)?(\d+)$/i.exec(ref);
  if (inc) return { kind: 'incident', ref: `INC-${Number(inc[1])}` };
  return { kind: 'other', ref };
}

// ---------------------------------------------------------------------------
// Age in working days (E4)
// ---------------------------------------------------------------------------

const MELBOURNE_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Australia/Melbourne',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** The Melbourne calendar day of an instant, as a UTC-midnight epoch (DST-safe). */
function melbourneDayEpoch(t: number): number {
  const [y, m, d] = MELBOURNE_DAY.format(new Date(t)).split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

const DAY_MS = 86_400_000;

/**
 * Working days (Mon–Fri, Melbourne calendar) after the day `iso` was written, up to and including
 * today. Written today → 0; written Friday, read Monday → 1. Public holidays are not excluded.
 * An unparseable date → null.
 */
export function workingDaysSince(iso: string | null | undefined, now: ActedAt | number): number | null {
  const from = Date.parse(iso ?? '');
  const to = new Date(now).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  const a = melbourneDayEpoch(from);
  const b = melbourneDayEpoch(to);
  if (b <= a) return 0;
  const days = Math.round((b - a) / DAY_MS);
  let count = Math.floor(days / 7) * 5;
  const startDow = new Date(a).getUTCDay();
  for (let i = 1; i <= days % 7; i++) {
    const dow = (startDow + i) % 7;
    if (dow !== 0 && dow !== 6) count++;
  }
  return count;
}

/** Whole calendar days between two instants' Melbourne days (for "3 days ago"). */
export function calendarDaysSince(iso: string | null | undefined, now: ActedAt | number): number | null {
  const from = Date.parse(iso ?? '');
  const to = new Date(now).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.max(0, Math.round((melbourneDayEpoch(to) - melbourneDayEpoch(from)) / DAY_MS));
}

// ---------------------------------------------------------------------------
// The linked Action Register row (ISS-188, the caller limb)
// ---------------------------------------------------------------------------

/**
 * What a Verify row asks, in plain words (13.1-14, ISS-241). The row's `Action` is the agent's verb
 * (`propose-action`, …); a Care Manager can't act on the verb, so the card leads with the question instead.
 */
export interface VlQuestion {
  /** Short heading for the row. */
  headline: string;
  /** One sentence: what to check, and what Verify and Reject mean. */
  ask: string;
  /** "What the agent wrote", in words. */
  label: string;
}

export function vlQuestion(action: string | null | undefined): VlQuestion {
  switch (action) {
    case 'propose-action':
      return {
        headline: 'Right action, right person?',
        ask:
          "An agent proposed this action. Verify it if it's the right action for the right person, with the right " +
          "owner and due date. If anything is wrong, reject it and say what.",
        label: 'Proposed an action',
      };
    case 'practice-check':
      return {
        headline: 'Practice check',
        ask: 'A practice row, so nothing real is touched: reject it with a reason, then correct it to Verify.',
        label: 'Practice row',
      };
    default:
      return {
        headline: 'Is this right?',
        ask: "An agent wrote this record. Verify if it matches the source; if not, reject it and say what's wrong.",
        label: action || 'No action recorded',
      };
  }
}

/** Action Register statuses that record a completion someone could self-verify. */
export const COMPLETED_STATUSES: readonly ActionStatus[] = ['Done', 'Verified'];

/** What the page knows about a row's linked Action Register row. */
export type LinkedState =
  | { status: 'none' }
  | { status: 'loading' }
  | { status: 'missing'; ref: string }
  | { status: 'error'; ref: string }
  | {
      status: 'ready';
      row: ActionRow;
      /** From `loadVerifyContext` — `'loading'` / `'error'` while it is not known. */
      completer: { completedByUpn?: string } | 'loading' | 'error' | 'not-needed';
    };

export type LinkRefusal = 'checking' | 'linked-missing' | 'linked-unreadable';

export type GateResult = { ok: true } | { ok: false; reason: LinkRefusal | AuthorityReason };

/** Does the linked row need its completer resolved before the evidence can be verified? */
export function needsCompleter(row: ActionRow): boolean {
  return COMPLETED_STATUSES.includes(row.status);
}

/**
 * May `actor` verify / reject a Verification Log row, given its linked Action Register row?
 *
 *   - Not linked to the Action Register → yes (the builders' own checks still apply).
 *   - Linked, and the action has a completion (`Done` / `Verified`) → `completerGate` (verify's
 *     release gate and completer limb) with the `completedByUpn` from `loadVerifyContext`
 *     (ISS-188). An unknown completer is refused (`completer-unknown`), never hidden; so is the
 *     person who completed it. WHO may give the VL verdict is the builders' question
 *     (`mayVerifyEvidence`, D-13-R-6), so this gate is not Oversight-only.
 *   - Linked, and nothing has been completed yet (e.g. the agent's `propose-action` evidence) →
 *     yes: there is no completion to self-verify, and E4's AgentIdentity check covers the writer.
 *   - The linked row is still loading, cannot be found or cannot be read → refused (fail closed).
 */
export function linkedActionGate(link: LinkedState, actor: Actor): GateResult {
  switch (link.status) {
    case 'none':
      return { ok: true };
    case 'loading':
      return { ok: false, reason: 'checking' };
    case 'missing':
      return { ok: false, reason: 'linked-missing' };
    case 'error':
      return { ok: false, reason: 'linked-unreadable' };
    case 'ready': {
      if (!needsCompleter(link.row)) return { ok: true };
      const c = link.completer;
      if (c === 'loading') return { ok: false, reason: 'checking' };
      if (c === 'error') return { ok: false, reason: 'linked-unreadable' };
      const completedByUpn = c === 'not-needed' ? undefined : c.completedByUpn;
      const r = completerGate(link.row, actor, { completedByUpn });
      return r.ok ? { ok: true } : { ok: false, reason: r.reason };
    }
  }
}

// ---------------------------------------------------------------------------
// Plain-language refusals
// ---------------------------------------------------------------------------

/** What the page says when a row cannot be verified or rejected. */
export function refusalText(code: string, linkedRef?: string): string {
  const action = linkedRef ? `the linked action ${linkedRef}` : 'the linked action';
  switch (code) {
    case 'completer-unknown':
      return `Who completed ${action} can't be confirmed, so this can't be verified here.`;
    case 'no-self-verification':
      return `You completed ${action}. Someone else verifies it.`;
    case 'not-released':
      return `${linkedRef ?? 'The linked action'} hasn't been released yet.`;
    case 'oversight-only':
      return 'Only Oversight verifies this row.';
    case 'not-verifier-role':
      return "This row is for another role to verify.";
    case 'not-verifier':
      return 'Only the person who gave this verdict, or Oversight, can correct it.';
    case 'outside-window':
      return 'Corrections after the day need Oversight in the register.';
    case 'no-change':
      return 'That is already the verdict.';
    case 'no-verdict':
      return 'This row has no verdict to correct.';
    case 'self-verification':
      return 'You wrote this row. Someone else verifies it.';
    case 'already-verified':
      return 'This row already has a verdict.';
    case 'reason-required':
      return `A rejection needs a reason of at least ${MIN_REASON_CHARS} characters.`;
    case 'checking':
      return `Checking who completed ${action}…`;
    case 'linked-missing':
      return `${linkedRef ?? 'The linked action'} can't be found in the Action Register.`;
    case 'linked-unreadable':
      return `${linkedRef ?? 'The linked action'} couldn't be read. Reload to try again.`;
    default:
      return "This row can't be changed here.";
  }
}
